import { useCallback, useMemo, useState } from "react";
import { slugify } from "helpers/slugify";
import rollbar from "helpers/rollbar";

export type ProductionGroupObject = {
    Id: string;
    Name: string;
};

export interface UseProductionGroupsAssignmentProps {
    allProductionGroups: string[];
    onSetSelectedProductionGroups?: (groups: string[]) => void;
    preselectedProductionGroups?: string[];
}

export const useProductionGroupsAssignment = ({
    allProductionGroups,
    onSetSelectedProductionGroups,
    preselectedProductionGroups = []
}: UseProductionGroupsAssignmentProps) => {
    const [productionGroups, setProductionGroups] =
        useState<string[]>(allProductionGroups);

    const [selectedProductionGroups, setSelectedProductionGroups] = useState<
        string[]
    >(preselectedProductionGroups);

    const onAddProductionGroup = useCallback(
        (newGroup: string) => {
            if (!productionGroups.includes(newGroup)) {
                setProductionGroups((prevGroups) => [...prevGroups, newGroup]);
            }
        },
        [productionGroups]
    );

    const allProductionGroupsAsObjects: ProductionGroupObject[] = useMemo(
        () =>
            productionGroups.map((pg) => ({
                Id: pg,
                Name: pg
            })),
        [productionGroups]
    );

    const selectedProductionGroupsAsObjects: ProductionGroupObject[] = useMemo(
        () =>
            selectedProductionGroups.map((pg) => ({
                Id: pg,
                Name: pg
            })),
        [selectedProductionGroups]
    );

    const setSelectedProductionGroupsFromUserInput = useCallback(
        (productionGroup: string) => {
            setSelectedProductionGroups((prev) => {
                const newGroups = [...prev, productionGroup];
                onSetSelectedProductionGroups?.(newGroups);
                return newGroups;
            });
        },
        [onSetSelectedProductionGroups]
    );

    const setSelectedProductionGroupsFromDropdown = useCallback(
        (productionGroupObj: ProductionGroupObject) => {
            const productionGroup = productionGroupObj.Id;
            setSelectedProductionGroups((prev) => {
                if (prev.includes(productionGroup)) {
                    return prev;
                }

                const newGroups = [...prev, productionGroup];
                onSetSelectedProductionGroups?.(newGroups);
                return newGroups;
            });
        },
        [onSetSelectedProductionGroups]
    );

    const handleAddProductionGroup = useCallback(
        async (newGroup: string) => {
            const existingGroup = allProductionGroups?.find(
                (pg) => slugify(pg) === slugify(newGroup)
            );

            if (newGroup !== "" && existingGroup) {
                // Group exists, just add to selected
                setSelectedProductionGroupsFromDropdown({
                    Id: existingGroup,
                    Name: existingGroup
                });
            } else if (newGroup !== "") {
                try {
                    // Add new group to global list first
                    onAddProductionGroup(newGroup);

                    // Then add to selected items for this instance
                    setSelectedProductionGroupsFromUserInput(newGroup);
                } catch (e) {
                    rollbar.error("Error adding user category", e);
                }
            }
        },
        [
            allProductionGroups,
            setSelectedProductionGroupsFromDropdown,
            setSelectedProductionGroupsFromUserInput,
            onAddProductionGroup
        ]
    );

    const handleRemoveProductionGroup = useCallback(
        (productionGroupId: string) => {
            setSelectedProductionGroups((prev) => {
                const newGroups = prev.filter((pg) => pg !== productionGroupId);
                onSetSelectedProductionGroups?.(newGroups);
                return newGroups;
            });
        },
        [onSetSelectedProductionGroups]
    );

    const handleAssignProductionGroup = useCallback(
        (productionGroupObj: ProductionGroupObject) => {
            setSelectedProductionGroupsFromDropdown(productionGroupObj);
        },
        [setSelectedProductionGroupsFromDropdown]
    );

    return {
        allProductionGroupsAsObjects,
        selectedProductionGroupsAsObjects,
        handleAddProductionGroup,
        handleAssignProductionGroup,
        handleRemoveProductionGroup
    };
};
